import React, { useState } from 'react';
import { Check, Zap, Award, Sparkles, Star, Lock, X, Shield, FileText, RotateCcw } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { User } from './types';
import { initiateCheckout } from './services/stripeService';

export const PlansView = ({ user, t, onUpgrade, onNavigate }: { user: User, t: any, onUpgrade?: (planId: string) => void, onNavigate?: (view: string) => void }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  // Calculate annual prices with discounts
  const calculateAnnualPrice = (monthlyPrice: number, discountPercent: number): string => {
    const annualPrice = (monthlyPrice * 12 * (1 - discountPercent / 100)).toFixed(2);
    return `€${annualPrice}`;
  };

  // Plan configuration with billing intervals
  const planConfig = {
    free: {
      monthlyPrice: 0,
      discountPercent: 0,
    },
    pro: {
      monthlyPrice: 14.99,
      discountPercent: 0, // No discount - annual is €99 flat
    },
    unlimited: {
      monthlyPrice: 14.99,
      discountPercent: 0, // No discount - annual is €99 flat
    }
  };

  const plans = [
    {
      id: 'free',
      name: t?.plans_free || 'FREE',
      price: '€0',
      period: t?.plans_period_always || '/sempre',
      priceYearly: '€0',
      periodYearly: t?.plans_period_always || '/sempre',
      discountPercent: 0,
      monthlyPrice: 0,
      features: [
        'Social Feed & Interaction',
        'Shop VitaCore Access',
        t?.plans_feature_xp || 'Sistema XP e Livelli base',
        t?.plans_feature_log || 'Quick Log Manuale illimitato',
        'Foto Progresso (max 10/mese)',
        'Leaderboard Globale'
      ],
      icon: <Award className="text-slate-500" />,
      color: 'border-slate-800 bg-slate-900/50',
      btnText: t?.plans_current_plan || 'PIANO ATTUALE'
    },
    {
      id: 'pro',
      name: t?.plans_pro || 'PRO',
      price: '€14.99',
      period: t?.plans_period_month || '/mese',
      priceYearly: '€99',
      periodYearly: t?.plans_period_year || '/year',
      discountPercent: 0,
      monthlyPrice: 14.99,
      trial: t?.plans_trial || '7-DAY FREE TRIAL',
      features: [
        t?.plans_feature_ai_coach || 'AI Coach (Conversazione illimitata)',
        t?.plans_feature_workout || 'AI Workout Plans & Generator',
        'Smartwatch Hub (Apple, Google, etc)',
        'Active Maps (Spot & GPS Bonus)',
        'Sfide Gruppali & Custom',
        'Badge Argento Esclusivo'
      ],
      icon: <Zap className="text-emerald-400" fill="currentColor" />,
      color: 'border-emerald-500 shadow-xl shadow-emerald-500/10 bg-slate-900',
      popular: true,
      btnText: t?.plans_button_upgrade || 'ATTIVA PRO'
    },
    {
      id: 'unlimited',
      name: t?.plans_unlimited || 'UNLIMITED',
      price: '€14.99',
      period: t?.plans_period_month || '/mese',
      priceYearly: '€99',
      periodYearly: t?.plans_period_year || '/year',
      discountPercent: 0,
      monthlyPrice: 14.99,
      features: [
        t?.plans_feature_all_pro || 'TUTTO DEL PIANO PRO',
        t?.plans_feature_diet || 'AI Diet Plans & Macro Tracker',
        t?.plans_feature_photos || 'Analisi Foto Pasti via AI',
        t?.plans_feature_community || 'Legends Only Community (Lvl 75+)',
        'Advanced Analytics (Trends & AI)',
        'Cornice Animata & Supporto VIP'
      ],
      icon: <Sparkles className="text-cyan-400" />,
      color: 'border-cyan-500 bg-slate-900 shadow-xl shadow-cyan-500/10',
      btnText: t?.plans_button_legend || 'DIVENTA LEGGENDA'
    }
  ];

  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  const handleSelectPlan = async (planId: string) => {
    // Prevent double-submit
    if (processingPlan !== null) return;
    
    if (user.plan === planId && user.billingPeriod === billingPeriod) return;
    
    setProcessingPlan(planId);
    
    try {
    if (onUpgrade) {
        // If onUpgrade is provided, it should handle the checkout
        // We'll need to modify App.tsx to pass billingPeriod, but for now use initiateCheckout directly
        if (planId !== 'free') {
          await initiateCheckout(planId, user.id, billingPeriod);
        } else {
          const userRef = doc(db, "users", user.id);
          await updateDoc(userRef, { plan: planId, planStatus: 'active', billingPeriod: undefined });
        }
    } else {
      const userRef = doc(db, "users", user.id);
        const updateData: { plan: string; planStatus: 'active'; billingPeriod?: 'monthly' | 'yearly' | undefined } = { 
          plan: planId, 
          planStatus: 'active' 
        };
        if (planId !== 'free') {
          updateData.billingPeriod = billingPeriod;
        } else {
          updateData.billingPeriod = undefined;
        }
        await updateDoc(userRef, updateData);
      }
    } catch (error) {
      console.error('[PLANS] Error selecting plan:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(t?.plans_error_checkout_start || `Failed to start checkout: ${errorMessage}`);
    } finally {
      setProcessingPlan(null);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-12 max-w-7xl mx-auto pb-32 animate-fadeIn">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-500 px-4 py-2 rounded-full text-xs font-black tracking-widest uppercase">
          <Star size={14} fill="currentColor"/> {t?.plans_upgrade_exp || "Upgrade Your Experience"}
        </div>
        <h1 className="text-5xl font-black tracking-tighter italic text-white uppercase">{t?.plans_title}</h1>
        <p className="text-slate-500 font-medium max-w-xl mx-auto">{t?.plans_subtitle}</p>
      </div>

      {/* Billing Period Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-4 bg-slate-900 border border-slate-800 rounded-full p-2">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-3 rounded-full text-sm font-black transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-emerald-500 text-white shadow-lg'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {t?.plans_billing_monthly || 'Monthly'}
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-6 py-3 rounded-full text-sm font-black transition-all ${
              billingPeriod === 'yearly'
                ? 'bg-emerald-500 text-white shadow-lg'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {t?.plans_billing_yearly || 'Annual'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {plans.map((p) => (
          <div key={p.id} className={`relative border-2 rounded-[3.5rem] p-10 flex flex-col transition-all duration-500 hover:scale-[1.02] ${p.color} ${user.plan === p.id ? 'ring-4 ring-emerald-500/20' : ''}`}>
            {p.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-400 text-white text-[10px] font-black px-6 py-2 rounded-full flex items-center gap-2 shadow-xl">
                <Zap size={12} fill="currentColor" /> {t?.plans_most_popular}
              </div>
            )}
            <div className="mb-10">
              <div className="w-16 h-16 bg-slate-800 rounded-3xl flex items-center justify-center mb-6 shadow-inner">{p.icon}</div>
              <h3 className="text-3xl font-black mb-2 tracking-tight">{p.name}</h3>
              <div className="space-y-1">
                {billingPeriod === 'yearly' && p.discountPercent > 0 && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-black text-slate-600 line-through">
                      €{(p.monthlyPrice * 12).toFixed(2)}
                    </span>
                    <span className="text-slate-500 text-xs">/year</span>
                  </div>
                )}
              <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-white">
                    {billingPeriod === 'yearly' && p.priceYearly ? p.priceYearly : p.price}
                  </span>
                  <span className="text-slate-500 text-sm font-bold">
                    {billingPeriod === 'yearly' && p.periodYearly ? p.periodYearly : p.period}
                  </span>
                </div>
              </div>
              {billingPeriod === 'yearly' && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="inline-block bg-orange-500/20 text-orange-400 text-[10px] font-black px-3 py-1 rounded-lg tracking-widest border border-orange-500/30">
                      {t('plans.annual_savings')}
                    </div>
                    <span className="text-[10px] text-slate-500 font-bold">
                      {t?.plans_billed_yearly || 'Billed yearly'}
                    </span>
                  </div>
                  <p className="text-[9px] text-slate-600 font-bold italic">
                    {t('plans.value_anchor')}
                  </p>
                </div>
              )}
              {/* Conditional Reward */}
              {billingPeriod === 'yearly' && (
                <div className="mt-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <p className="text-xs font-black text-emerald-400 uppercase tracking-widest text-center">
                    {t('plans.conditional_reward')}
                  </p>
                </div>
              )}
              {p.trial && billingPeriod === 'monthly' && (
                <div className="mt-3 inline-block bg-emerald-500/10 text-emerald-400 text-[10px] font-black px-3 py-1 rounded-lg tracking-widest border border-emerald-500/20">
                  {p.trial}
                </div>
              )}
            </div>
            <div className="flex-1 space-y-5 mb-10">
              {p.features.map((f, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="mt-1 p-1 bg-slate-800 rounded-full text-emerald-500 shadow-sm"><Check size={12} strokeWidth={4} /></div>
                  <span className="text-sm font-bold text-slate-300 leading-snug">{f}</span>
                </div>
              ))}
            </div>
            <button 
              onClick={() => handleSelectPlan(p.id)} 
              disabled={user.plan === p.id && user.billingPeriod === billingPeriod} 
              className={`w-full py-5 rounded-2xl font-black transition-all transform active:scale-95 shadow-2xl ${
                user.plan === p.id && user.billingPeriod === billingPeriod
                  ? 'bg-slate-800 text-slate-500 cursor-default border border-slate-700'
                  : p.id === 'unlimited'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-cyan-500/40'
                  : 'bg-white text-black hover:bg-emerald-500 hover:text-white'
              }`}
            >
              {user.plan === p.id && user.billingPeriod !== billingPeriod
                ? (t?.plans_switch_plan || 'Switch Plan')
                : p.btnText}
            </button>
            {user.plan === p.id && user.billingPeriod && user.billingPeriod !== billingPeriod && (
              <p className="mt-2 text-[10px] text-slate-500 text-center italic">
                {t?.plans_current_subscription || 'You can switch plans from your account settings'}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="hidden md:block mt-20 bg-slate-900/80 border border-slate-800 rounded-[3rem] overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-800/50">
              <th className="p-6 text-xs font-black uppercase tracking-widest text-slate-400">{t?.plans_feature_label || "Funzionalità"}</th>
              <th className="p-6 text-center text-xs font-black uppercase tracking-widest text-slate-400">Free</th>
              <th className="p-6 text-center text-xs font-black uppercase tracking-widest text-emerald-400">Pro</th>
              <th className="p-6 text-center text-xs font-black uppercase tracking-widest text-cyan-400">Unlimited</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {[
              { label: t?.plans_feature_ai_coach, f: true, p: true, u: true },
              { label: 'Smartwatch Sync', f: false, p: true, u: true },
              { label: 'Active Maps GPS', f: false, p: true, u: true },
              { label: t?.plans_feature_diet, f: false, p: false, u: true },
              { label: t?.plans_feature_community, f: false, p: false, u: true }
            ].map((row, i) => (
              <tr key={i} className="hover:bg-slate-800/20 transition-colors">
                <td className="p-6 text-sm font-bold text-slate-300">{row.label}</td>
                <td className="p-6 text-center">{row.f ? <Check className="mx-auto text-emerald-500"/> : <X className="mx-auto text-slate-700"/>}</td>
                <td className="p-6 text-center">{row.p ? <Check className="mx-auto text-emerald-500"/> : <X className="mx-auto text-slate-700"/>}</td>
                <td className="p-6 text-center">{row.u ? <Check className="mx-auto text-emerald-500"/> : <X className="mx-auto text-slate-700"/>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legal Links for Stripe Compliance */}
      <div className="mt-12 p-6 bg-slate-900/50 border border-slate-800 rounded-2xl">
        <p className="text-xs text-slate-500 text-center mb-4 font-bold uppercase tracking-widest">
          By proceeding, you agree to our
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={() => onNavigate?.('terms')}
            className="flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors font-bold uppercase tracking-wider"
          >
            <FileText size={14} />
            Terms & Conditions
          </button>
          <span className="text-slate-700">•</span>
          <button
            onClick={() => onNavigate?.('privacy')}
            className="flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors font-bold uppercase tracking-wider"
          >
            <Shield size={14} />
            Privacy Policy
          </button>
          <span className="text-slate-700">•</span>
          <button
            onClick={() => onNavigate?.('refund')}
            className="flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors font-bold uppercase tracking-wider"
          >
            <RotateCcw size={14} />
            Refund Policy
          </button>
        </div>
      </div>
    </div>
  );
};
